import React, { useState } from "react";
import { Form, Input, Button, message, Upload, DatePicker, Card } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useAuth } from "../Context";
import { useNotification } from "../NotificationContext";
import Title from "antd/es/typography/Title";
import VehiculosSelect from "./VehiculosSelect";
import { addDoc, collection, updateDoc, doc } from "firebase/firestore";
import { firestore, storage } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import moment from "moment";

const AgregarCarga = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { sucursal, currentUser } = useAuth();
  const openNotificationWithIcon = useNotification();

  const handleFinish = async (values) => {
    if (!sucursal) {
      message.error("Error: No se pudo obtener la sucursal");
      return;
    }

    setLoading(true);
    try {
      // Calcular consumo y rendimiento
      const kmRecorridos = values.finalMileage - values.initialMileage;
      const rendimiento = kmRecorridos / values.liters;
      const costoPorKm = values.price / kmRecorridos;

      const cargaData = {
        vehiculoId: values.vehicle,
        fecha: values.fecha?.toDate() || new Date(),
        kilometrajeInicial: parseInt(values.initialMileage),
        kilometrajeFinal: parseInt(values.finalMileage),
        kmRecorridos,
        litros: parseFloat(values.liters),
        precio: parseFloat(values.price),
        precioPorLitro: parseFloat(values.price) / parseFloat(values.liters),
        rendimiento, // km por litro
        costoPorKm,
        ticketUrl: values.ticketUrl || null,
        observaciones: values.observaciones || "",
        registradoPor: currentUser?.email || "",
        creadoEn: new Date(),
        sucursal
      };

      // Guardar en Firestore
      const docRef = await addDoc(
        collection(firestore, `sucursal/${sucursal}/carga`),
        cargaData
      );

      // Actualizar kilometraje del vehículo
      if (values.vehicle) {
        await updateDoc(
          doc(firestore, `sucursal/${sucursal}/vehiculos/${values.vehicle}`),
          {
            kilometrajeActual: parseInt(values.finalMileage),
            ultimaCarga: new Date()
          }
        );
      }

      message.success("Carga registrada con éxito");
      openNotificationWithIcon("success", "Éxito", "La carga de combustible se registró correctamente");
      form.resetFields();
    } catch (error) {
      console.error("Error al registrar carga:", error);
      message.error("Error al registrar la carga");
      openNotificationWithIcon("error", "Error", "Hubo un problema al registrar la carga");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `cargas/${sucursal}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      form.setFieldsValue({ ticketUrl: downloadURL });
      message.success("Imagen subida correctamente");
    } catch (error) {
      console.error("Error al subir imagen:", error);
      message.error("Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card style={{ maxWidth: 800, margin: "20px auto" }}>
      <Title level={3} className="ccg-center">
        Registrar Carga de Gasolina
      </Title>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          fecha: moment()
        }}
      >
        <Form.Item
          name="fecha"
          label="Fecha de la Carga"
          rules={[{ required: true, message: "Seleccione la fecha" }]}
        >
          <DatePicker 
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder="Seleccionar fecha"
          />
        </Form.Item>

        <Form.Item
          name="vehicle"
          label="Vehículo"
          rules={[{ required: true, message: "Seleccione un vehículo" }]}
        >
          <VehiculosSelect placeholder="Seleccionar vehículo" />
        </Form.Item>

        <Form.Item
          name="initialMileage"
          label="Kilometraje Inicial"
          rules={[
            { required: true, message: "Ingrese el kilometraje inicial" },
            { type: "number", min: 0, message: "Debe ser un número positivo" }
          ]}
        >
          <Input 
            type="number" 
            placeholder="Ej: 15000" 
            addonAfter="km"
          />
        </Form.Item>

        <Form.Item
          name="finalMileage"
          label="Kilometraje Final"
          rules={[
            { required: true, message: "Ingrese el kilometraje final" },
            { type: "number", min: 0, message: "Debe ser un número positivo" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const initialMileage = getFieldValue('initialMileage');
                if (!value || !initialMileage || value > initialMileage) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('El kilometraje final debe ser mayor al inicial'));
              },
            }),
          ]}
        >
          <Input 
            type="number" 
            placeholder="Ej: 15200" 
            addonAfter="km"
          />
        </Form.Item>

        <Form.Item
          name="liters"
          label="Litros de Combustible"
          rules={[
            { required: true, message: "Ingrese los litros" },
            { type: "number", min: 0.1, message: "Debe ser mayor a 0" }
          ]}
        >
          <Input 
            type="number" 
            step="0.01"
            placeholder="Ej: 40.5" 
            addonAfter="L"
          />
        </Form.Item>

        <Form.Item
          name="price"
          label="Precio Total"
          rules={[
            { required: true, message: "Ingrese el precio total" },
            { type: "number", min: 0.01, message: "Debe ser mayor a 0" }
          ]}
        >
          <Input 
            type="number" 
            step="0.01"
            placeholder="Ej: 850.50" 
            addonBefore="$"
          />
        </Form.Item>

        <Form.Item
          name="observaciones"
          label="Observaciones (Opcional)"
        >
          <Input.TextArea 
            rows={3}
            placeholder="Ej: Carga completa, gasolinera X, etc."
          />
        </Form.Item>

        <Form.Item
          name="ticketUrl"
          label="Foto del Ticket"
          rules={[{ required: true, message: "Suba la foto del ticket" }]}
        >
          <Upload
            accept="image/*"
            beforeUpload={(file) => {
              handleUpload(file);
              return false; // Prevent automatic upload
            }}
            maxCount={1}
            listType="picture"
          >
            <Button 
              icon={<UploadOutlined />} 
              loading={uploading}
              disabled={uploading}
            >
              {uploading ? "Subiendo..." : "Subir Foto del Ticket"}
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            disabled={loading || uploading}
            size="large"
            style={{ width: "100%" }}
          >
            {loading ? "Registrando..." : "Registrar Carga"}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default AgregarCarga;
